import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Which slice of history the charts cover. */
private enum class StatsPeriod(val label: String, val apiValue: String) {
    All("Celkově", "all"),
    Month("Měsíc", "month"),
    Week("Týden", "week"),
}

/**
 * One colour per exercise type, shared by every chart on this page so a given exercise always
 * shows in the same colour. Hues step around the wheel by the golden angle (137.508°) rather than
 * picking from a fixed list, so however many exercise types get added later, each new one still
 * lands visually far from its neighbours instead of repeating or clustering.
 */
private fun colorFor(type: ExerciseType, allTypes: List<ExerciseType>): String {
    val index = allTypes.indexOf(type).coerceAtLeast(0)
    val hue = (index * 137.508) % 360.0
    return "hsl(${hue.toInt()}, 55%, 45%)"
}

/**
 * Four views on the same history (SPECIFIKACE.md ch. 8 point 3 follow-up), each switchable
 * between all time / this month / this week; the exercise breakdown and tier distribution are
 * also switchable between everyone combined and one participant. The per-participant composition
 * chart has no such toggle -- comparing one person against themselves says nothing -- so it hides
 * itself whenever a single participant is selected instead.
 *
 * PostgreSQL returns the four chart-sized aggregates for the selected period/person. Switching a
 * filter therefore makes one small RPC request instead of downloading the ever-growing entry
 * history and grouping it in the browser.
 */
@Composable
fun StatisticsScreen(token: String) {
    val state = rememberAsyncState()
    var participants by remember { mutableStateOf<List<Participant>>(emptyList()) }
    var exerciseTypes by remember { mutableStateOf<List<ExerciseType>>(emptyList()) }
    var metadataLoaded by remember { mutableStateOf(false) }
    var statistics by remember { mutableStateOf<StatisticsSnapshot?>(null) }
    var period by remember { mutableStateOf(StatsPeriod.All) }
    var selectedParticipantId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(period, selectedParticipantId) {
        state.run {
            if (!metadataLoaded) {
                // Historical statistics keep deactivated people/exercises: their stored points
                // remain part of the leaderboard and must still have names in these charts.
                participants = Api.fetchParticipants(includeInactive = true)
                exerciseTypes = Api.fetchExerciseTypes(includeInactive = true)
                metadataLoaded = true
            }
            statistics = null
            statistics = Api.fetchStatistics(token, period.apiValue, selectedParticipantId, todayIso())
        }
    }

    ErrorMessage(state.error)

    val snapshot = statistics
    if (!state.loaded || snapshot == null) {
        LoadingText()
        return
    }

    val today = todayIso()
    val periodStart = when (period) {
        StatsPeriod.All -> null
        StatsPeriod.Month -> startOfMonthIso(today)
        StatsPeriod.Week -> startOfWeekIso(today)
    }
    Div(attrs = { classes("tab-row") }) {
        StatsPeriod.entries.forEach { candidate ->
            NavButton(candidate.label, period == candidate) { period = candidate }
        }
    }
    Div(attrs = { classes("tab-row") }) {
        NavButton("Všichni", selectedParticipantId == null) { selectedParticipantId = null }
        participants.forEach { participant ->
            NavButton(participant.name, selectedParticipantId == participant.id) {
                selectedParticipantId = participant.id
            }
        }
    }

    ExerciseBreakdown(snapshot.exercisePoints, exerciseTypes)
    TrendOverTime(snapshot.trend, period, periodStart, today)
    TierDistribution(snapshot.tierCounts)
    // A comparison of one person against themselves says nothing, so this one stays hidden while
    // a specific participant is selected above.
    if (selectedParticipantId == null) {
        ParticipantComposition(snapshot.participantComposition, participants, exerciseTypes)
    }
}

/** Section 1: how points split across exercise types. */
@Composable
private fun ExerciseBreakdown(points: List<StatisticsExercisePoints>, exerciseTypes: List<ExerciseType>) {
    val typesById = exerciseTypes.associateBy { it.id }
    val pointsByType = points.mapNotNull { row ->
        typesById[row.exerciseTypeId]?.let { it to row.points }
    }
    val maxPoints = pointsByType.maxOfOrNull { (_, points) -> points } ?: 0.0
    val totalPoints = pointsByType.sumOf { (_, points) -> points }

    Div(attrs = { classes("board") }) {
        H3 { Text("Rozdělení bodů podle cviků") }

        if (pointsByType.isEmpty()) {
            P { Text("V tomhle období nejsou žádné body.") }
        } else {
            Div(attrs = { classes("chart") }) {
                pointsByType.forEach { (type, points) ->
                    Div(attrs = { classes("chart-row") }) {
                        Span(attrs = { classes("chart-label") }) { Text(type.name) }
                        Div(attrs = { classes("chart-bar-track") }) {
                            Div(attrs = {
                                classes("chart-bar-fill")
                                style {
                                    width((points / maxPoints * 100).coerceIn(2.0, 100.0).percent)
                                    backgroundColor(Color(colorFor(type, exerciseTypes)))
                                }
                            }) {}
                        }
                        Span(attrs = { classes("chart-value") }) {
                            val share = if (totalPoints > 0) points / totalPoints * 100 else 0.0
                            Text("${formatNumber(points)} b · ${formatNumber(share)} %")
                        }
                    }
                }
            }
        }
    }
}

/** Section 2: points per day (week/month) or per month (all time), so a trend is visible instead
 * of just one summed total. */
@Composable
private fun TrendOverTime(points: List<StatisticsTrendPoint>, period: StatsPeriod, periodStart: String?, today: String) {
    val pointsByBucket = points.associate { it.bucket to it.points }
    val buckets: List<Pair<String, Double>> = when (period) {
        StatsPeriod.Week -> (0..6).map { offset ->
            val date = shiftDaysIso(periodStart!!, offset)
            date to (pointsByBucket[date] ?: 0.0)
        }
        StatsPeriod.Month -> datesBetween(periodStart!!, today).map { date -> date to (pointsByBucket[date] ?: 0.0) }
        StatsPeriod.All -> points.map { it.bucket to it.points }
    }
    val label: (String) -> String = when (period) {
        StatsPeriod.Week -> ::shortDayNameFor
        StatsPeriod.Month -> { date -> date.substring(8, 10).toInt().toString() }
        StatsPeriod.All -> { month -> "${month.substring(5, 7).toInt()}/${month.substring(2, 4)}" }
    }
    val maxPoints = buckets.maxOfOrNull { (_, points) -> points } ?: 0.0

    Div(attrs = { classes("board") }) {
        H3 { Text("Trend aktivity v čase") }

        if (buckets.isEmpty() || maxPoints == 0.0) {
            P { Text("V tomhle období nejsou žádné body.") }
        } else {
            Div(attrs = { classes("trend-scroll") }) {
                Div(attrs = { classes("trend-chart") }) {
                    buckets.forEach { (key, points) ->
                        Div(attrs = { classes("trend-col") }) {
                            Div(attrs = { classes("trend-bar-track") }) {
                                Div(attrs = {
                                    classes("trend-bar-fill")
                                    style { height((points / maxPoints * 100).coerceIn(0.0, 100.0).percent) }
                                }) {}
                            }
                            Span(attrs = { classes("trend-label") }) { Text(label(key)) }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Section 3: how many days landed in each rating tier -- the quality behind the point totals.
 *
 * The RPC already includes missing calendar days in the zero-point tier. It computes them as the
 * calendar span minus logged daily-summary rows, so no raw days need to cross the network.
 */
@Composable
private fun TierDistribution(tierRows: List<StatisticsTierCount>) {
    val totalDays = tierRows.sumOf { it.days }
    val maxCount = tierRows.maxOfOrNull { it.days } ?: 0

    Div(attrs = { classes("board") }) {
        H3 { Text("Rozdělení dní podle hodnocení") }

        if (tierRows.isEmpty()) {
            P { Text("V tomhle období nejsou žádné zapsané dny.") }
        } else {
            Div(attrs = { classes("chart") }) {
                tierRows.forEach { row ->
                    Div(attrs = { classes("chart-row") }) {
                        Span(attrs = { classes("chart-label") }) { Text(row.label) }
                        Div(attrs = { classes("chart-bar-track") }) {
                            Div(attrs = {
                                classes("chart-bar-fill", tierBadgeClass(row.label))
                                style { width((row.days.toDouble() / maxCount * 100).coerceIn(2.0, 100.0).percent) }
                            }) {}
                        }
                        Span(attrs = { classes("chart-value") }) {
                            val share = if (totalDays > 0) row.days.toDouble() / totalDays * 100 else 0.0
                            Text("${row.days} dní · ${formatNumber(share)} %")
                        }
                    }
                }
            }
        }
    }
}

/** Section 4: each participant's own exercise mix, side by side -- who focuses on what. Only
 * rendered while the page's participant toggle is on "Všichni" (see [StatisticsScreen]). */
@Composable
private fun ParticipantComposition(
    points: List<StatisticsCompositionPoint>,
    participants: List<Participant>,
    exerciseTypes: List<ExerciseType>,
) {
    data class Row(val participant: Participant, val segments: List<Pair<ExerciseType, Double>>, val total: Double)

    val pointsByKey = points.associate { (it.participantId to it.exerciseTypeId) to it.points }
    val rows = participants
        .map { participant ->
            val segments = exerciseTypes
                .map { it to (pointsByKey[participant.id to it.id] ?: 0.0) }
                .filter { (_, points) -> points > 0 }
            Row(participant, segments, segments.sumOf { (_, points) -> points })
        }
        .filter { it.total > 0 }
        .sortedByDescending { it.total }
    val usedTypes = exerciseTypes.filter { type -> rows.any { row -> row.segments.any { it.first == type } } }

    Div(attrs = { classes("board") }) {
        H3 { Text("Skladba cviků podle účastníka") }

        if (rows.isEmpty()) {
            P { Text("V tomhle období nejsou žádné body.") }
        } else {
            Div(attrs = { classes("chart-legend") }) {
                usedTypes.forEach { type ->
                    Span(attrs = { classes("legend-item") }) {
                        Span(attrs = {
                            classes("legend-dot")
                            style { backgroundColor(Color(colorFor(type, exerciseTypes))) }
                        }) {}
                        Text(type.name)
                    }
                }
            }
            Div(attrs = { classes("chart") }) {
                rows.forEach { row ->
                    Div(attrs = { classes("chart-row") }) {
                        Span(attrs = { classes("chart-label") }) { Text(row.participant.name) }
                        Div(attrs = { classes("stacked-bar-track") }) {
                            row.segments.forEach { (type, points) ->
                                Div(attrs = {
                                    classes("stacked-bar-segment")
                                    style {
                                        width((points / row.total * 100).percent)
                                        backgroundColor(Color(colorFor(type, exerciseTypes)))
                                    }
                                }) {}
                            }
                        }
                        Span(attrs = { classes("chart-value") }) { Text("${formatNumber(row.total)} b") }
                    }
                }
            }
        }
    }
}
